import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");   // ✅ gets token from URL

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();

        if (password !== confirm) {
            setMessage("Passwords do not match");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_ROOT}/api/auth/reset-password`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token, new_password: password }),
                }
            );

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Reset failed");

            setMessage(data.msg || "Password reset successful!");
        } catch (err) {
            setMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow">
                <h1 className="text-2xl font-semibold mb-4">
                    Create a new password
                </h1>

                {message && (
                    <div className={`mb-4 text-sm p-3 rounded ${message.includes('successful') ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
                        {message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="New password"
                            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
                            title="Password must be at least 8 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character (e.g. Test@123)."
                            className="w-full p-2 border rounded-md pr-10 focus:ring-2 focus:ring-indigo-300 outline-none"
                        />
                        <button 
                            type="button" 
                            onClick={() => setShowPassword(!showPassword)} 
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    <div className="relative">
                        <input
                            type={showConfirm ? "text" : "password"}
                            required
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Confirm password"
                            className="w-full p-2 border rounded-md pr-10 focus:ring-2 focus:ring-indigo-300 outline-none"
                        />
                        <button 
                            type="button" 
                            onClick={() => setShowConfirm(!showConfirm)} 
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                        {loading ? "Saving..." : "Save password"}
                    </button>
                </form>
            </div>
        </div>
    );
}
